import api from './api';

export interface Imputable {
    nroCuenta: number;
    nroRubro?: number;
    nomCuenta?: string;
    tipoCuentaImputa?: string;
    descripcioncta?: string | null;
    visibleEn?: string;
    saldo?: number;
}

export interface ImputableCRUD {
    codImputable: string;
    nombre: string;
    nivel: number;
    imputable: string;
    tipo: string;
    activo: string;
    codImputablePadre?: string;
    descripcion?: string;
    nroRubro?: number;
    saldo?: number | null;
}

export interface ImputableSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
}

export interface ImputablePagedResult {
    items: ImputableCRUD[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface ImputableSearchPagedResult {
    items: Imputable[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const imputableService = {
    getImputables: async (params?: ImputableSearchParams): Promise<Imputable[]> => {
        const response = await api.get('/api/Imputables', params ? { params } : {});
        const data = response.data;
        
        if (Array.isArray(data)) {
            return data;
        }
        
        if (data && typeof data === 'object' && 'items' in data) {
            return data.items;
        }
        
        return [];
    },

    getImputablesSearchPaged: async (params: ImputableSearchParams): Promise<ImputableSearchPagedResult> => {
        const response = await api.get('/api/Imputables', { params });
        const data = response.data;
        const items: Imputable[] = Array.isArray(data?.items) ? data.items : [];
        const totalCount = data?.totalCount ?? 0;
        const pageSize = data?.pageSize ?? params.pageSize;
        const totalPages = (data?.totalPages ?? Math.ceil(totalCount / pageSize)) || 1;
        return {
            items,
            totalCount,
            pageNumber: data?.pageNumber ?? params.pageNumber,
            pageSize,
            totalPages
        };
    },

    getImputablesPaged: async (params: ImputableSearchParams): Promise<ImputablePagedResult> => {
        const response = await api.get('/api/Imputables', { params });
        const data = response.data;
        
        // Función para transformar Imputable a ImputableCRUD
        const transformItem = (item: any): ImputableCRUD => ({
            codImputable: item.codImputable || item.nroCuenta?.toString() || '',
            nombre: item.nombre || item.nomCuenta || '',
            nivel: item.nivel ?? 0,
            imputable: item.imputable || (item.tipoCuentaImputa ? 'S' : 'N'),
            tipo: item.tipo || item.tipoCuentaImputa || '',
            activo: item.activo ?? 'S',
            codImputablePadre: item.codImputablePadre,
            descripcion: item.descripcion || item.descripcioncta || '',
            nroRubro: item.nroRubro
        });
        
        if (data && typeof data === 'object' && 'items' in data) {
            const transformedItems = Array.isArray(data.items) ? data.items.map(transformItem) : [];
            return {
                items: transformedItems,
                totalCount: data.totalCount || transformedItems.length,
                pageNumber: data.pageNumber || params.pageNumber,
                pageSize: data.pageSize || params.pageSize,
                totalPages: data.totalPages || Math.ceil((data.totalCount || transformedItems.length) / params.pageSize)
            };
        }
        
        if (Array.isArray(data)) {
            const transformedItems = data.map(transformItem);
            return {
                items: transformedItems,
                totalCount: data.length,
                pageNumber: params.pageNumber,
                pageSize: params.pageSize,
                totalPages: Math.ceil(data.length / params.pageSize)
            };
        }
        
        return {
            items: [],
            totalCount: 0,
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            totalPages: 0
        };
    },

    getImputable: async (nroCuenta: number | string): Promise<(ImputableCRUD & Imputable) | null> => {
        try {
            const response = await api.get<Imputable>(`/api/Imputables/${nroCuenta}`);
            const item = response.data;
            if (!item) return null;
            const nro = item.nroCuenta ?? Number(nroCuenta);
            const nom = item.nomCuenta || '';
            return {
                ...item,
                codImputable: item.nroCuenta?.toString() || String(nroCuenta),
                nombre: nom,
                nivel: 0,
                imputable: item.tipoCuentaImputa ? 'S' : 'N',
                tipo: item.tipoCuentaImputa || '',
                activo: item.visibleEn ?? 'S',
                codImputablePadre: undefined,
                descripcion: item.descripcioncta || '',
                nroRubro: item.nroRubro,
                saldo: item.saldo != null ? Number(item.saldo) : undefined,
                nroCuenta: nro,
                nomCuenta: nom
            };
        } catch (error) {
            console.error('Error al obtener imputable:', error);
            return null;
        }
    },

    createImputable: async (imputable: ImputableCRUD): Promise<ImputableCRUD> => {
        const response = await api.post<ImputableCRUD>('/api/Imputables', imputable);
        return response.data;
    },

    updateImputable: async (codImputable: string, imputable: ImputableCRUD): Promise<void> => {
        const payload = {
            nroRubro: imputable.nroRubro ?? 0,
            nomCuenta: imputable.nombre,
            tipoCuentaImputa: imputable.tipo || 'A',
            descripcioncta: imputable.descripcion ?? null,
            visibleEn: imputable.activo === 'S' ? 'S' : null,
            saldo: imputable.saldo ?? null
        };
        await api.put(`/api/Imputables/${codImputable}`, payload);
    },

    deleteImputable: async (codImputable: string): Promise<void> => {
        await api.delete(`/api/Imputables/${codImputable}`);
    }
};

export default imputableService;

